// 1Rotary — 3-4 year historical datasets
// Drives long-horizon trend widgets across modules

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const SEASONALITY = [0.86, 0.94, 1.20, 1.34, 1.30, 1.10, 0.96, 0.92, 0.94, 1.04, 1.10, 1.00];

// Generate Feb 2022 → Jan 2026 (48 months) with growth + seasonality + small noise (deterministic)
function noise(i) {
  const x = Math.sin(i * 12.9898 + 78.233) * 43758.5453;
  return (x - Math.floor(x)) * 0.06 - 0.03; // ±3%
}

export const HISTORICAL_MONTHLY = (() => {
  const out = [];
  const startYear = 2022;
  const startMonth = 1; // Feb (0-indexed)
  const base = 92_000_000; // ₱92M baseline at start
  const monthlyGrowth = 0.0098; // ~12% YoY compounded
  for (let i = 0; i < 48; i++) {
    const month = (startMonth + i) % 12;
    const year = startYear + Math.floor((startMonth + i) / 12);
    const trend = base * Math.pow(1 + monthlyGrowth, i);
    const seasonal = trend * SEASONALITY[month] * (1 + noise(i));
    const revenue = Math.round(seasonal);
    const cogs = Math.round(revenue * (0.692 - i * 0.0005 + noise(i + 100) * 0.05));
    const target = Math.round(trend * SEASONALITY[month] * 0.99);
    out.push({
      key: `${year}-${String(month + 1).padStart(2, "0")}`,
      label: `${MONTH_LABELS[month]} ${String(year).slice(2)}`,
      year, monthIndex: month, month: MONTH_LABELS[month],
      revenue, cogs, target,
      grossProfit: revenue - cogs,
      grossMargin: (revenue - cogs) / revenue,
    });
  }
  return out;
})();

// 16-quarter cashflow history
export const HISTORICAL_QUARTERLY_CASH = (() => {
  const out = [];
  let opening = 64_500_000; // ₱64.5M opening Q1-2022
  for (let i = 0; i < 16; i++) {
    const year = 2022 + Math.floor(i / 4);
    const q = (i % 4) + 1;
    const seasonalBoost = q === 2 ? 1.20 : q === 1 ? 1.04 : q === 3 ? 0.92 : 0.96;
    const inflow = Math.round((118_000_000 + i * 2_400_000) * seasonalBoost * (1 + noise(i) * 0.5));
    const outflow = Math.round((114_000_000 + i * 2_280_000) * seasonalBoost * (1 + noise(i + 50) * 0.4));
    const net = inflow - outflow;
    const closing = opening + net;
    out.push({
      key: `${year}-Q${q}`, year, quarter: q,
      opening, inflow, outflow, net, closing,
    });
    opening = closing;
  }
  return out;
})();

// Yearly KPI history (4 years)
export const HISTORICAL_KPI = [
  { year: 2022, revenue: 1_268_000_000, gm: 0.298, ebitda: 0.094, forecastAcc: 0.682, otd: 0.788, stockoutPct: 0.078, turnover: 7.6, dso: 48, ccc: 36 },
  { year: 2023, revenue: 1_452_000_000, gm: 0.306, ebitda: 0.104, forecastAcc: 0.718, otd: 0.812, stockoutPct: 0.068, turnover: 8.2, dso: 45, ccc: 33 },
  { year: 2024, revenue: 1_628_000_000, gm: 0.314, ebitda: 0.112, forecastAcc: 0.749, otd: 0.828, stockoutPct: 0.061, turnover: 8.8, dso: 43, ccc: 31 },
  { year: 2025, revenue: 1_842_000_000, gm: 0.320, ebitda: 0.119, forecastAcc: 0.782, otd: 0.842, stockoutPct: 0.054, turnover: 9.4, dso: 42, ccc: 29 },
];

// Branch count evolution (network grew from 62 → 86)
export const HISTORICAL_NETWORK = [
  { year: 2022, branches: 62, brands: 2, regions: 4, headcount: 740 },
  { year: 2023, branches: 71, brands: 3, regions: 5, headcount: 868 },
  { year: 2024, branches: 78, brands: 3, regions: 5, headcount: 948 },
  { year: 2025, branches: 86, brands: 3, regions: 5, headcount: 1_034 },
];

// NPI history (going back to 2022)
export const NPI_HISTORY = [
  { id: "NPI-2022-014", sku: "OEM Vios Compressor Gen-2", launch: "2022-Q2", status: "Mature", peakUnits: 4_280, gmEnd: 0.31, outcome: "Success" },
  { id: "NPI-2022-021", sku: "Foam Filter Roll Eco", launch: "2022-Q3", status: "Decline", peakUnits: 3_140, gmEnd: 0.42, outcome: "Cannibalized" },
  { id: "NPI-2023-008", sku: "Inverter Compressor 1.5HP", launch: "2023-Q1", status: "Mature", peakUnits: 8_240, gmEnd: 0.36, outcome: "Hero SKU" },
  { id: "NPI-2023-019", sku: "Freon R32 9.5kg (Direct Import)", launch: "2023-Q3", status: "Mature", peakUnits: 6_180, gmEnd: 0.34, outcome: "Replaced R22" },
  { id: "NPI-2024-005", sku: "Universal Aircon Control PCB", launch: "2024-Q1", status: "Growth", peakUnits: 2_840, gmEnd: 0.39, outcome: "Scaling" },
  { id: "NPI-2024-022", sku: "Anti-Bacterial Filter Roll Premium", launch: "2024-Q4", status: "Growth", peakUnits: 6_240, gmEnd: 0.41, outcome: "Outperforming" },
  { id: "NPI-2025-003", sku: "Inverter Compressor 2.0HP — Daikin", launch: "2025-Q3", status: "Pilot", peakUnits: 124, gmEnd: 0.31, outcome: "On-track" },
  { id: "NPI-2025-018", sku: "EcoCool R290 Refrigerant 6kg", launch: "2025-Q4", status: "Scale", peakUnits: 1_840, gmEnd: 0.38, outcome: "On-track" },
];

// Past promotion outcomes — 12 quarters
export const PROMO_OUTCOMES = (() => {
  const labels = ["3.3", "4.4", "Mid-Year", "6.6", "9.9", "10.10", "11.11", "12.12"];
  const out = [];
  for (let q = 0; q < 12; q++) {
    const year = 2023 + Math.floor(q / 4);
    const quarter = (q % 4) + 1;
    out.push({
      key: `${year}-Q${quarter}-${labels[q % labels.length]}`,
      name: `Shopee ${labels[q % labels.length]} ${year}`,
      year, quarter,
      revenue: Math.round((28_000_000 + q * 1_400_000) * (1 + noise(q) * 0.4)),
      uplift: +(1.4 + q * 0.04 + noise(q + 7)).toFixed(2),
      cannibalization: +(0.14 + noise(q + 17)).toFixed(2),
      gmImpact: +(-0.04 + noise(q + 22) * 0.5).toFixed(3),
    });
  }
  return out;
})();
